import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { IconKrzyzyk, IconOstrzezenie, IconUpload } from "../icons/index.js";
import { ProgressBar } from "./ProgressBar.js";
import { Tooltip } from "./Tooltip.js";

/**
 * UP-1..UP-4: strefa wgrywania i lista plikow.
 *
 * ODSTEPSTWO OD GOTOWCA, swiadome: upload.html daje strefie
 * `background: var(--surface-sunken)`, ale ten gotowiec jest STARSZY niz regula T-5.
 * T-5 mowi wprost, ze duzy element na karcie nie lezy w szarym wglebieniu, i wymienia
 * UP-1/UP-3 wsrod korygowanych. Strefa zostaje wiec przy przerywanym obrysie, ale BEZ
 * wypelnienia, a lista plikow idzie wzorcem .rowlist (polaczona ramka + hairline'y).
 * Male kontrolki - w tym pola formularza - dalej sa sunken; T-5 wylacza je wprost.
 *
 * UP-1: wymagania widoczne OD RAZU, nie dopiero po bledzie (P-4).
 * UP-2: walidacja na miejscu, komunikat w stylu F-12 mowi, co poprawic.
 * UP-4: strefa wylaczona zawsze z POWODEM (S-10), nie samym wygaszeniem.
 */
export function UploadZone({
  wymagania,
  accept,
  onPlik,
  wylaczona = false,
  powodWylaczenia,
  blad,
  tytul = "Przeciągnij plik tutaj lub kliknij, aby wybrać",
}: {
  /** UP-1: dopuszczalne formaty i limit, np. "WAV, MP3, M4A - maks. 200 MB". */
  wymagania: string;
  accept?: string;
  onPlik: (plik: File) => void;
  wylaczona?: boolean;
  powodWylaczenia?: string;
  /** UP-2: komunikat walidacji przy strefie. */
  blad?: string;
  tytul?: string;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [nadStrefa, setNadStrefa] = useState(false);
  const baza = useId();
  const idWymagan = `${baza}-wymagania`;
  const idPowodu = `${baza}-powod`;
  const idBledu = `${baza}-blad`;

  const opisy = [idWymagan, powodWylaczenia ? idPowodu : null, blad ? idBledu : null].filter(Boolean).join(" ");

  function otworzWybor(): void {
    if (wylaczona) return;
    inputRef.current?.click();
  }

  return (
    <div className="upload-zone-wrap">
      <div
        className={`upload-zone${nadStrefa ? " is-dragover" : ""}${wylaczona ? " is-disabled" : ""}`}
        role="button"
        tabIndex={wylaczona ? -1 : 0}
        aria-disabled={wylaczona || undefined}
        aria-describedby={opisy}
        onClick={otworzWybor}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            otworzWybor();
          }
        }}
        onDragOver={(e) => {
          if (wylaczona) return;
          e.preventDefault();
          setNadStrefa(true);
        }}
        onDragLeave={() => setNadStrefa(false)}
        onDrop={(e) => {
          e.preventDefault();
          setNadStrefa(false);
          if (wylaczona) return;
          const plik = e.dataTransfer?.files?.[0];
          if (plik) onPlik(plik);
        }}
      >
        <span className="upload-zone__icon" aria-hidden="true">
          <IconUpload size={32} />
        </span>
        <p className="upload-zone__title">{tytul}</p>
        <p className="upload-zone__hint" id={idWymagan}>
          {wymagania}
        </p>
        {powodWylaczenia && wylaczona && (
          <p className="upload-zone__disabled-msg" id={idPowodu}>
            {powodWylaczenia}
          </p>
        )}
      </div>

      <input
        ref={inputRef}
        className="upload-input"
        type="file"
        accept={accept}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const plik = e.target.files?.[0];
          if (plik) onPlik(plik);
          // Bez tego wybranie TEGO SAMEGO pliku drugi raz nie odpali onChange.
          e.target.value = "";
        }}
      />

      {blad && (
        <p className="upload-error" id={idBledu}>
          <IconOstrzezenie size={14} />
          {blad}
        </p>
      )}
    </div>
  );
}

export interface WierszPliku {
  id: string;
  nazwa: string;
  /** Rozmiar albo status w --text-muted, np. "12,4 MB". */
  meta?: string;
  /** UP-3: mini pasek postepu w wierszu, gdy plik sie przetwarza. */
  postep?: number;
  blad?: string;
}

/**
 * UP-3 po korekcie T-5: lista plikow to JEDNA polaczona ramka z hairline'ami,
 * a nie stos szarych prostokatow. Usuniecie jest nieodwracalne, wiec wywolujacy
 * ma podpiac pod `onUsun` potwierdzenie D-4 (ConfirmDialog), nie kasowac od razu.
 */
export function ListaPlikow({
  pliki,
  onUsun,
  ikona,
}: {
  pliki: readonly WierszPliku[];
  onUsun?: (id: string) => void;
  ikona?: ReactNode;
}): React.JSX.Element {
  return (
    <ul className="rowlist">
      {pliki.map((plik) => (
        <li className="rowlist__row" key={plik.id}>
          {ikona && (
            <span className="rowlist__ikona" aria-hidden="true">
              {ikona}
            </span>
          )}
          <div className="rowlist__body">
            <p className="rowlist__nazwa">{plik.nazwa}</p>
            {plik.meta && <p className="rowlist__meta">{plik.meta}</p>}
            {plik.postep !== undefined && (
              <div className="rowlist__postep">
                <ProgressBar etykieta={`Wgrywanie pliku ${plik.nazwa}`} procent={plik.postep} />
              </div>
            )}
            {plik.blad && (
              <p className="rowlist__blad">
                <IconOstrzezenie size={14} />
                {plik.blad}
              </p>
            )}
          </div>
          {onUsun && (
            <div className="rowlist__actions">
              <Tooltip tekst={`Usuń plik ${plik.nazwa}`}>
                <button
                  type="button"
                  className="btn btn--danger btn--ikona"
                  aria-label={`Usuń plik ${plik.nazwa}`}
                  onClick={() => onUsun(plik.id)}
                >
                  <IconKrzyzyk size={16} />
                </button>
              </Tooltip>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
